import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Loading,
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { marketsForScope } from '../dataProvider/types';
import type {
  AdminDataProvider,
  CmsPageMarket,
  SeoRule,
} from '../dataProvider';

/**
 * The SEO rule authoring form (D-cms-testimonials-seo). A FLAT structured CRUD form, NO
 * rich text and NO publish lifecycle (meta is set, not authored-then-published). The
 * pageKey is a FREE path / key string (e.g. 'about', 'blog/cashblack-launches-uk', 'home',
 * 'stores'): it is NOT a FK and is NOT validated against any collection, because it may
 * name a system route the CMS does not own and it must cover all four page-identifier
 * shapes uniformly. The region variant IS the market: the same pageKey is authored once
 * per market (a uk row and a us row are two rules). Authoring gates cms:author,
 * operational market-scoped.
 */

const SeoRuleFormInner = ({ record }: { record?: SeoRule }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const { identity } = useGetIdentity();
  const { activeRegion } = useRegion();

  const isEdit = record != null;
  const markets = useMemo(
    () => marketsForScope(activeRegion ?? 'GLOBAL'),
    [activeRegion],
  );
  // A rule targets a single in-scope market OR both (the region variant is the market).
  const marketChoices: CmsPageMarket[] = [...markets, 'all'];

  const [pageKey, setPageKey] = useState(record?.pageKey ?? '');
  const [metaTitle, setMetaTitle] = useState(record?.metaTitle ?? '');
  const [metaDescription, setMetaDescription] = useState(
    record?.metaDescription ?? '',
  );
  const [ogImageUrl, setOgImageUrl] = useState(record?.ogImageUrl ?? '');
  const [market, setMarket] = useState<CmsPageMarket>(
    record?.market ?? marketChoices[0] ?? 'uk',
  );

  const validate = (): string | null => {
    if (!pageKey.trim()) return 'A page key is required';
    if (!metaTitle.trim()) return 'A meta title is required';
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      notify(problem, { type: 'error' });
      return;
    }
    const nowIso = new Date().toISOString();
    const shared = {
      pageKey: pageKey.trim(),
      metaTitle,
      metaDescription,
      ogImageUrl,
      market,
      updatedAt: nowIso,
    };
    try {
      if (isEdit && record) {
        await dataProvider.update('seoRules', {
          id: record.id,
          data: shared,
          previousData: record,
        });
        notify('SEO rule saved', { type: 'success' });
        redirect('list', 'seoRules');
      } else {
        const { data } = await dataProvider.create('seoRules', {
          data: {
            ...shared,
            createdBy: String(identity?.email ?? identity?.id ?? 'unknown'),
            createdAt: nowIso,
          },
        });
        notify('SEO rule created', { type: 'success' });
        redirect('edit', 'seoRules', data.id);
      }
    } catch {
      notify('Could not save the SEO rule', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEdit ? 'Edit SEO rule' : 'New SEO rule'}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Page key"
              value={pageKey}
              onChange={(e) => setPageKey(e.target.value)}
              helperText="A free page path, e.g. about, blog/my-post, home, stores"
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'seo-page-key' }}
            />
            <TextField
              select
              label="Market"
              value={market}
              onChange={(e) => setMarket(e.target.value as CmsPageMarket)}
              margin="dense"
              size="small"
              sx={{ minWidth: 140 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'seo-market' }}
            >
              {marketChoices.map((m) => (
                <option key={m} value={m}>
                  {m === 'all' ? 'Both markets' : m.toUpperCase()}
                </option>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Meta title"
            value={metaTitle}
            onChange={(e) => setMetaTitle(e.target.value)}
            margin="dense"
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'seo-meta-title' }}
          />
          <TextField
            label="Meta description"
            value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value)}
            margin="dense"
            size="small"
            fullWidth
            multiline
            minRows={2}
            inputProps={{ 'data-testid': 'seo-meta-description' }}
          />
          <TextField
            label="OG image URL"
            value={ogImageUrl}
            onChange={(e) => setOgImageUrl(e.target.value)}
            helperText="A URL to the social share image (no upload)"
            margin="dense"
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'seo-og-image' }}
          />
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={save} data-testid="seo-save">
          {isEdit ? 'Save' : 'Create SEO rule'}
        </Button>
        <Button onClick={() => redirect('list', 'seoRules')}>Cancel</Button>
      </Stack>
    </Box>
  );
};

/** Create view. Exported for testing without the access wrapper. */
export const SeoRuleCreateView = () => <SeoRuleFormInner />;

/** Edit view. Exported for testing without the access wrapper. */
export const SeoRuleEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<SeoRule>('seoRules', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;
  return <SeoRuleFormInner record={record} />;
};

export const SeoRuleCreate = () => (
  <RequireAccess capability="cms:author">
    <SeoRuleCreateView />
  </RequireAccess>
);

export const SeoRuleEdit = () => (
  <RequireAccess capability="cms:author">
    <SeoRuleEditView />
  </RequireAccess>
);
